import { useCallback, useMemo } from "react";

import { SlidersHorizontal } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type Group = {
  id: string;
  name: string;
};

function toggleArrayItem<T>(available: T[], array: T[], item: T): T[] {
  if (!available.includes(item)) {
    return array;
  }

  if (array.length === 1 && array[0] === "all") {
    return [item];
  }

  if (array.includes(item)) {
    return array.filter((i) => i !== item);
  }
  const result = [...array, item];
  if (result.length === available.length) {
    return ["all" as T];
  }
  return result;
}

export function GroupFilter({
  selectedGroupIds,
  filteringGroups = false,
  availableGroups,
  onSelectedGroupIdsChange,
}: {
  availableGroups: Group[];
  selectedGroupIds: string[];
  onSelectedGroupIdsChange: (groupIds: string[]) => void;
  filteringGroups?: boolean;
}) {
  const availableGroupIds = useMemo(
    () => availableGroups.map((group) => group.id),
    [availableGroups],
  );

  const handleSelectAll = useCallback(
    () => onSelectedGroupIdsChange(["all"]),
    [onSelectedGroupIdsChange],
  );

  const toggleGroupHandlers = useMemo(
    () =>
      Object.fromEntries(
        availableGroupIds.map((groupId) => [
          groupId,
          () =>
            onSelectedGroupIdsChange(
              toggleArrayItem(availableGroupIds, selectedGroupIds, groupId),
            ),
        ]),
      ),
    [availableGroupIds, selectedGroupIds, onSelectedGroupIdsChange],
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button intent="secondary" variant="outline" disabled={filteringGroups}>
          <SlidersHorizontal />{" "}
          {selectedGroupIds.length === 1 && selectedGroupIds[0] === "all"
            ? "Group"
            : selectedGroupIds.length === 1
              ? availableGroups.find(
                  (group) => group.id === selectedGroupIds[0],
                )?.name
              : `${selectedGroupIds.length} Groups`}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuCheckboxItem
          checked={
            selectedGroupIds.length === 1 && selectedGroupIds[0] === "all"
          }
          onCheckedChange={handleSelectAll}
        >
          All
        </DropdownMenuCheckboxItem>
        {availableGroups.map((group) => (
          <DropdownMenuCheckboxItem
            key={group.id}
            checked={selectedGroupIds.includes(group.id)}
            onCheckedChange={toggleGroupHandlers[group.id]}
          >
            {group.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
